import { Component } from '@theme/component';
import { push, EVENTS } from '@theme/analytics';

/**
 * The curated collection grid: client-side filtering over a fully rendered stream.
 *
 * Every product and every promo block is already in the DOM — under 60 items, filtering is a
 * `hidden` attribute away and there is no reason to hit the network. Stock Horizon's facets do a
 * Section Rendering round trip, which is right for a 585-product catalogue and wrong for this one.
 *
 * **This is an enhancement, not the mechanism.** The section applies the identical rule in Liquid
 * from `?filter=`, so the page is correct with JavaScript disabled or still loading. The chips are
 * real links; all this does is skip the navigation. One markup path, and no flash of the wrong
 * items.
 *
 * `replaceState`, never `pushState` (§6): a shareable URL, without ten chip clicks burying the
 * page someone arrived from. The lightbox deliberately does the opposite.
 *
 * @see docs/build-brief-collection-page.md — §4, §5, §6, §11
 */

/**
 * @typedef {Object} CuratedRefs
 * @property {HTMLElement} [grid]
 * @property {HTMLElement[]} [items]
 * @property {HTMLElement[]} [promos]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [emptyState]
 */

/**
 * @extends {Component<CuratedRefs>}
 */
class CuratedCollection extends Component {
  requiredRefs = ['grid'];

  /** @type {number} Column count the promos are currently placed against. */
  #placedColumns = 0;

  /** @type {string} */
  #filter = 'all';

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // The server cannot read `?filter=` — Liquid exposes no query string — so it always renders
    // the full grid and the filter is applied here on connect. That is also why an unrecognised
    // value has to be rejected here rather than in Liquid: without this, a stale or hand-edited
    // URL would hide every product and the page would read as broken rather than as a bad link.
    const requested = new URL(window.location.href).searchParams.get('filter') || 'all';
    const known = ['all', ...(this.dataset.categories || '').split(',').filter(Boolean)];

    this.#filter = known.includes(requested) ? requested : 'all';

    if (this.#filter !== 'all') {
      this.#applyFilter(this.#filter);
      this.#syncChips(this.#filter);
      this.#promoteImages();
    }

    // The server placed promos against a 4-column layout because it cannot know the viewport.
    // Re-place for the real one before anything else.
    this.#placePromos(true);

    // Column count changes on resize and — at >=990px — when a drawer squeezes `.page-wrapper`,
    // which is a real reflow this page will see every time the shortlist opens.
    this.#resizeObserver = new ResizeObserver(() => this.#placePromos());
    if (this.refs.grid) this.#resizeObserver.observe(this.refs.grid);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  onChipClick(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-filter-value]') : null;
    if (!(target instanceof HTMLAnchorElement)) return;

    // Let the browser handle anything that isn't a plain left click — cmd-click to a new tab, and
    // middle click, both still work, because the chip is a real link and not a button in disguise.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    this.filter(target.dataset.filterValue || 'all');
  }

  /**
   * @param {MouseEvent} event
   * @returns {void}
   */
  onPromoClick(event) {
    const link = event.target instanceof Element ? event.target.closest('[data-promo-link]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    push(EVENTS.promoClick, {
      block_position: link.dataset.promoPosition ?? '',
      link_url: link.getAttribute('href') ?? '',
    });
  }

  /**
   * @param {string} value
   * @returns {void}
   */
  filter(value) {
    this.#filter = value;

    const visible = this.#applyFilter(value);

    this.#syncChips(value);
    this.#updateUrl(value);
    this.#placePromos(true);
    this.#promoteImages();
    this.#announce(visible);

    push(EVENTS.collectionFilter, { filter_value: value, result_count: visible });
  }

  /**
   * The matching rule.
   *
   * **This is written twice — here and in `sections/curated-collection.liquid`.** They must stay
   * identical; a divergence shows up as the page changing what it shows the moment JS loads. The
   * test plan checks a filtered URL with JS off against the same URL with JS on.
   *
   * Category only. Bespoke pieces used to be kept out of the category chips and given one of
   * their own; they are now filtered like everything else, so a bespoke piece shows under a
   * category chip if it carries that category, and only under All if it carries none. `kind` is
   * deliberately not consulted: it stays in the data, not in what anyone sees.
   *
   * @param {string} value
   * @returns {number} How many products matched
   */
  #applyFilter(value) {
    let visible = 0;

    for (const item of this.refs.items ?? []) {
      const categories = (item.dataset.category || '').split(',');
      const matches = value === 'all' || categories.includes(value);

      item.hidden = !matches;
      if (matches) visible++;
    }

    // Promo blocks do not render into an otherwise empty grid (§5 rule 4).
    for (const promo of this.refs.promos ?? []) {
      const allowed = (promo.dataset.filters || 'all').split(',');
      promo.hidden = visible === 0 || !allowed.includes(value);
    }

    if (this.refs.emptyState) this.refs.emptyState.hidden = visible > 0;

    return visible;
  }

  /**
   * Moves promo blocks to their snapped index among the currently visible products.
   *
   * **Only promos move. Products never do.** That is what keeps DOM order equal to visual order,
   * and therefore tab and screen-reader order equal to what is on screen — the property
   * `grid-auto-flow: dense` would have cost us, and which `sections/projects-grid.liquid` already
   * rejected dense over.
   *
   * @param {boolean} [force] - Re-place even if the column count has not changed
   * @returns {void}
   */
  #placePromos(force = false) {
    const { grid, promos } = this.refs;
    if (!grid || !promos?.length) return;

    const columns = this.#columnCount();
    if (!force && columns === this.#placedColumns) return;
    this.#placedColumns = columns;

    const visibleItems = (this.refs.items ?? []).filter((item) => !item.hidden);

    for (const promo of promos) {
      if (promo.hidden) continue;

      const position = Number(promo.dataset.position ?? 0);

      // Nearest row boundary, so a spanning block never starts mid-row and leaves a ragged gap.
      let snapped = Math.round(position / columns) * columns;

      // Past the end of the current stream: clamp rather than vanish (§5 rule 2).
      if (snapped > visibleItems.length) snapped = visibleItems.length;

      // `snapped` counts products, so the anchor is the next one after it. Undefined means the end
      // of the grid, which `insertBefore(node, null)` handles as an append.
      const anchor = visibleItems[snapped] ?? null;

      // Collisions: two blocks on one index render in block order, the second immediately after
      // the first, because they are processed in order and each lands before the same anchor.
      if (anchor !== promo.nextElementSibling || promo.parentElement !== grid) {
        grid.insertBefore(promo, anchor);
      }
    }
  }

  /**
   * Upgrades the first visible row to eager loading after a filter change.
   *
   * `loading` is decided server-side from the *unfiltered* position, so a product sitting fortieth
   * in the catalogue is lazy — and filtering can put it first on screen, where it pops in blank.
   * The browser does fetch it once it intersects, but the gap is visible, and on this page the
   * filtered view is often the first thing anyone properly looks at.
   *
   * @returns {void}
   */
  #promoteImages() {
    const visible = (this.refs.items ?? []).filter((item) => !item.hidden).slice(0, 8);

    for (const item of visible) {
      for (const image of item.querySelectorAll('img[loading="lazy"]')) {
        image.setAttribute('loading', 'eager');
      }
    }
  }

  /**
   * @returns {number}
   */
  #columnCount() {
    const { grid } = this.refs;
    if (!grid) return 1;

    const template = getComputedStyle(grid).gridTemplateColumns;
    const count = template.split(' ').filter(Boolean).length;

    return count > 0 ? count : 1;
  }

  /**
   * @param {string} value
   * @returns {void}
   */
  #syncChips(value) {
    // Scoped to the chip row: other things carry `data-filter-value` — the empty state's "view
    // all" link, for one — and they are not chips and must not be marked current.
    for (const chip of this.querySelectorAll('.curated-filters [data-filter-value]')) {
      if (!(chip instanceof HTMLElement)) continue;

      const active = chip.dataset.filterValue === value;
      chip.toggleAttribute('data-active', active);

      if (active) chip.setAttribute('aria-current', 'true');
      else chip.removeAttribute('aria-current');
    }
  }

  /**
   * @param {string} value
   * @returns {void}
   */
  #updateUrl(value) {
    const url = new URL(window.location.href);

    if (value === 'all') url.searchParams.delete('filter');
    else url.searchParams.set('filter', value);

    history.replaceState(history.state, '', url);
  }

  /**
   * @param {number} count
   * @returns {void}
   */
  #announce(count) {
    const { status } = this.refs;
    if (!status) return;

    const template = count === 1 ? this.dataset.countOne : this.dataset.countTemplate;
    status.textContent = (template ?? '[count]').replace('[count]', String(count));
  }
}

if (!customElements.get('curated-collection')) {
  customElements.define('curated-collection', CuratedCollection);
}
